import { useEffect, useState } from 'react'
import { TextField } from '@cinesend/ui-kit'
import AppDialog from '../app_dialog'
import useStore from '../../hooks/use_store'

const EditItemModal = ({ collection, item = null, languages = null, localizedValues = [], onClose }) => {
  const { createModel, updateModel } = useStore()
  const isLocalizable = Boolean(languages?.length)
  const [identifier, setIdentifier] = useState(item?.label ?? '')
  const [labelsByLanguage, setLabelsByLanguage] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isLocalizable) return
    const next = {}
    languages.forEach((language) => {
      const existing = localizedValues.find(
        (value) => value.language_id === language.id && value.collection_value_id === item?.id
      )
      next[language.id] = existing?.label ?? ''
    })
    setLabelsByLanguage(next)
  }, [isLocalizable, languages, localizedValues, item?.id])

  const isCreate = !item
  const canSave = identifier.trim().length > 0

  const save = async () => {
    const label = identifier.trim()
    if (!label || saving) return
    setSaving(true)

    try {
      if (!isLocalizable) {
        if (item) {
          if (label !== item.label) {
            await updateModel.mutateAsync({
              endpoint: 'collection-values',
              id: item.id,
              data: { label },
            })
          }
        } else {
          await createModel.mutateAsync({
            endpoint: 'collection-values',
            data: { collection_id: collection.id, label },
          })
        }
        onClose()
        return
      }

      let identifierId = item?.id
      if (item) {
        if (label !== item.label) {
          await updateModel.mutateAsync({
            endpoint: 'collection-values',
            id: item.id,
            data: { label },
          })
        }
      } else {
        const created = await createModel.mutateAsync({
          endpoint: 'collection-values',
          data: { collection_id: collection.id, label },
        })
        identifierId = created?.data?.collection_value?.id ?? created?.data?.id
      }

      if (!identifierId) {
        onClose()
        return
      }

      for (const language of languages) {
        const nextLabel = (labelsByLanguage[language.id] ?? '').trim()
        const existing = localizedValues.find(
          (value) => value.language_id === language.id && value.collection_value_id === (item?.id ?? identifierId)
        )
        if (existing) {
          if (nextLabel !== (existing.label ?? '')) {
            await updateModel.mutateAsync({
              endpoint: 'collection-values',
              id: existing.id,
              data: { label: nextLabel },
            })
          }
        } else if (nextLabel) {
          await createModel.mutateAsync({
            endpoint: 'collection-values',
            data: {
              collection_id: collection.id,
              language_id: language.id,
              label: nextLabel,
              collection_value_id: identifierId,
            },
          })
        }
      }

      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppDialog
      title={isCreate ? 'Create item' : 'Edit item'}
      onClose={onClose}
      confirmText={isCreate ? 'Create' : 'Save'}
      confirmDisabled={!canSave || saving}
      onConfirm={save}
    >
      <TextField
        label={isLocalizable ? 'Identifier label' : 'Value'}
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        autoFocus
      />
      {isLocalizable
        ? languages.map((language) => (
            <TextField
              key={language.id}
              label={`${language.eng_description} label`}
              value={labelsByLanguage[language.id] ?? ''}
              onChange={(e) =>
                setLabelsByLanguage((prev) => ({
                  ...prev,
                  [language.id]: e.target.value,
                }))
              }
            />
          ))
        : null}
    </AppDialog>
  )
}

export default EditItemModal
